import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
  type Firestore,
} from 'firebase/firestore'
import { firestoreDb } from '../firebase'
import type { HelperAssignedItem } from './helperAssignedItemsService'
import {
  HELPER_BLOCKER_ACTIVITY_ACTIONS,
  HELPER_BLOCKER_REASON_LABELS,
  type HelperBlockerReason,
  type HelperOpenBlocker,
} from '../../types/helperBlocker'
import { queueChecklistComment, queueWorkspaceActivity } from './queueService'

type Actor = { id: string; name: string }

const BLOCKER_PREFIX = '[BLOCKER ·'

const sanitizeNote = (value: string): string => value.trim().slice(0, 1200)

const blockerBody = (reason: HelperBlockerReason, note: string) => {
  const label = HELPER_BLOCKER_REASON_LABELS[reason]
  const trimmed = sanitizeNote(note)
  return `${BLOCKER_PREFIX} ${label}] ${trimmed || label}`
}

export const submitHelperBlockerReport = async (input: {
  item: Pick<HelperAssignedItem, 'id' | 'workspaceId' | 'parentContainerId' | 'title' | 'updatedAtIso'>
  actor: Actor
  reason: HelperBlockerReason
  note: string
  pageKey?: string | null
}): Promise<void> => {
  const note = sanitizeNote(input.note)
  await queueChecklistComment({
    workspaceId: input.item.workspaceId,
    checklistItemId: input.item.id,
    parentContainerId: input.item.parentContainerId ?? input.item.id,
    body: blockerBody(input.reason, note),
    actorId: input.actor.id,
    actorName: input.actor.name,
    assignedAt: input.item.updatedAtIso ?? null,
    pageKey: input.pageKey ?? null,
  })
  await queueWorkspaceActivity({
    workspaceId: input.item.workspaceId,
    actorId: input.actor.id,
    actorName: input.actor.name,
    targetType: 'CHECKLIST',
    targetId: input.item.id,
    targetTitle: input.item.title,
    action: 'HELPER_BLOCKER_RAISED',
    summary: `Helper reported a blocker on "${input.item.title}".`,
    metadata: {
      reason: input.reason,
      note,
      itemId: input.item.id,
      itemTitle: input.item.title,
      pageKey: input.pageKey ?? null,
    },
  })
}

export const submitHelperReplenishmentRequest = async (input: {
  item: Pick<HelperAssignedItem, 'id' | 'workspaceId' | 'parentContainerId' | 'title' | 'updatedAtIso'>
  actor: Actor
  stockItemIds: string[]
  shortagesById: HelperOpenBlocker['shortagesById']
  note?: string
  pageKey?: string | null
}): Promise<void> => {
  const note = sanitizeNote(input.note ?? '')
  const stockItemIds = Array.from(new Set(input.stockItemIds.filter(Boolean)))
  await queueChecklistComment({
    workspaceId: input.item.workspaceId,
    checklistItemId: input.item.id,
    parentContainerId: input.item.parentContainerId ?? input.item.id,
    body: blockerBody('MISSING_STOCK', note || 'Need more stock before I can continue.'),
    actorId: input.actor.id,
    actorName: input.actor.name,
    assignedAt: input.item.updatedAtIso ?? null,
    pageKey: input.pageKey ?? null,
  })
  await queueWorkspaceActivity({
    workspaceId: input.item.workspaceId,
    actorId: input.actor.id,
    actorName: input.actor.name,
    targetType: 'CHECKLIST',
    targetId: input.item.id,
    targetTitle: input.item.title,
    action: 'HELPER_REPLENISHMENT_REQUESTED',
    summary: `Helper requested replenishment for "${input.item.title}".`,
    metadata: {
      reason: 'MISSING_STOCK',
      note,
      itemId: input.item.id,
      itemTitle: input.item.title,
      stockItemIds,
      shortagesById: input.shortagesById,
      pageKey: input.pageKey ?? null,
    },
  })
}

const toMillis = (value: unknown): number => {
  if (!value) return 0
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  if (typeof value === 'object' && value && 'toMillis' in value && typeof value.toMillis === 'function') {
    return value.toMillis()
  }
  if (typeof value === 'object' && value && 'toDate' in value && typeof value.toDate === 'function') {
    return value.toDate().getTime()
  }
  return 0
}

const normalizeReason = (value: unknown): HelperBlockerReason =>
  typeof value === 'string' && value in HELPER_BLOCKER_REASON_LABELS ? value as HelperBlockerReason : 'OTHER'

const normalizeShortages = (value: unknown): HelperOpenBlocker['shortagesById'] =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as HelperOpenBlocker['shortagesById']
    : {}

const activityToBlocker = (id: string, data: Record<string, unknown>): HelperOpenBlocker | null => {
  if (!HELPER_BLOCKER_ACTIVITY_ACTIONS.includes(data.action as never)) return null
  const metadata = data.metadata && typeof data.metadata === 'object' ? data.metadata as Record<string, unknown> : {}
  return {
    id,
    workspaceId: String(data.workspaceId ?? ''),
    itemId: String(data.targetId ?? metadata.itemId ?? ''),
    itemTitle: String(data.targetTitle ?? metadata.itemTitle ?? 'Checklist item'),
    actorId: String(data.actorId ?? ''),
    actorName: typeof data.actorName === 'string' ? data.actorName : null,
    action: data.action as HelperOpenBlocker['action'],
    reason: normalizeReason(metadata.reason),
    note: typeof metadata.note === 'string' ? metadata.note : '',
    createdAtMs: toMillis(data.createdAt),
    stockItemIds: Array.isArray(metadata.stockItemIds) ? metadata.stockItemIds.map(String).filter(Boolean) : [],
    shortagesById: normalizeShortages(metadata.shortagesById),
  }
}

export const projectOpenHelperBlockers = (
  rows: Array<{ id: string; data: Record<string, unknown> }>,
  itemIds: readonly string[],
): HelperOpenBlocker[] => {
  const allowedItemIds = new Set(itemIds)
  const resolved = new Set(
    rows.flatMap((row) => {
      const data = row.data
      if (data.action !== 'RISK_RESOLVED') return []
      const metadata = data.metadata && typeof data.metadata === 'object' ? data.metadata as Record<string, unknown> : {}
      const linkedId = typeof metadata.linkedActivityId === 'string' ? metadata.linkedActivityId : null
      return linkedId ? [linkedId] : []
    }),
  )
  return rows
    .map((row) => activityToBlocker(row.id, row.data))
    .filter((entry): entry is HelperOpenBlocker =>
      Boolean(entry && allowedItemIds.has(entry.itemId) && !resolved.has(entry.id)),
    )
    .sort((a, b) => b.createdAtMs - a.createdAtMs)
}

export const subscribeOpenHelperBlockers = (
  workspaceId: string,
  actorId: string | null | undefined,
  itemIds: readonly string[],
  callback: (items: HelperOpenBlocker[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
) => {
  const uniqueItemIds = Array.from(new Set(itemIds.filter(Boolean)))
  if (!workspaceId || !actorId || uniqueItemIds.length === 0) {
    callback([])
    return () => {}
  }
  let ownRows: Array<{ id: string; data: Record<string, unknown> }> = []
  let resolutionRows: Array<{ id: string; data: Record<string, unknown> }> = []
  const emit = () => callback(projectOpenHelperBlockers([...ownRows, ...resolutionRows], uniqueItemIds))
  const ownQuery = query(
    collection(db, 'workspace_activity'),
    where('workspaceId', '==', workspaceId),
    where('actorId', '==', actorId),
    where('action', 'in', [...HELPER_BLOCKER_ACTIVITY_ACTIONS]),
  )
  const resolutionQuery = query(
    collection(db, 'workspace_activity'),
    where('workspaceId', '==', workspaceId),
    where('action', '==', 'RISK_RESOLVED'),
    where('metadata.targetActorId', '==', actorId),
  )
  const stopOwn = onSnapshot(
    ownQuery,
    (snapshot) => {
      ownRows = snapshot.docs.map((entry) => ({ id: entry.id, data: entry.data() as Record<string, unknown> }))
      emit()
    },
    (error) => {
      onError?.(error)
    },
  )
  const stopResolved = onSnapshot(
    resolutionQuery,
    (snapshot) => {
      resolutionRows = snapshot.docs.map((entry) => ({ id: entry.id, data: entry.data() as Record<string, unknown> }))
      emit()
    },
    (error) => {
      onError?.(error)
    },
  )
  return () => {
    stopOwn()
    stopResolved()
  }
}

export const resolveHelperBlocker = async (input: {
  workspaceId: string
  blocker: HelperOpenBlocker
  actor: Actor
  note?: string
  db?: Firestore
}): Promise<void> => {
  const { recordWorkspaceActivity } = await import('../activityService')
  await recordWorkspaceActivity(input.db ?? firestoreDb, {
    workspaceId: input.workspaceId,
    actorId: input.actor.id,
    actorName: input.actor.name,
    targetType: 'CHECKLIST',
    targetId: input.blocker.itemId,
    targetTitle: input.blocker.itemTitle,
    action: 'RISK_RESOLVED',
    summary: `Helper request resolved for "${input.blocker.itemTitle}".`,
    metadata: {
      linkedActivityId: input.blocker.id,
      targetActorId: input.blocker.actorId,
      note: sanitizeNote(input.note ?? ''),
    },
  })
}

export const fetchOpenHelperBlockers = async (
  workspaceId: string,
  actorId: string,
  itemIds: readonly string[],
  db: Firestore = firestoreDb,
): Promise<HelperOpenBlocker[]> => {
  const [ownSnap, resolvedSnap] = await Promise.all([
    getDocs(query(
      collection(db, 'workspace_activity'),
      where('workspaceId', '==', workspaceId),
      where('actorId', '==', actorId),
      where('action', 'in', [...HELPER_BLOCKER_ACTIVITY_ACTIONS]),
    )),
    getDocs(query(
      collection(db, 'workspace_activity'),
      where('workspaceId', '==', workspaceId),
      where('action', '==', 'RISK_RESOLVED'),
      where('metadata.targetActorId', '==', actorId),
    )),
  ])
  return projectOpenHelperBlockers(
    [...ownSnap.docs, ...resolvedSnap.docs].map((entry) => ({ id: entry.id, data: entry.data() as Record<string, unknown> })),
    itemIds,
  )
}
